import { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import type { Point } from '@/types';

/** Style for the drawn boundary line */
const BOUNDARY_STYLE = {
  strokeColor: 'rgba(255, 255, 255, 0.6)',
  lineWidth: 4,
};

/** Style for the line being drawn (before completion) */
const DRAWING_STYLE = {
  strokeColor: 'rgba(255, 255, 255, 0.5)',
  lineWidth: 4,
};

export interface BoundaryCanvasProps {
  /** Viewport width */
  width: number;
  /** Viewport height */
  height: number;
  /** Completed boundary points (closed loop) */
  boundaryPoints: Point[] | null;
  /** Points currently being drawn (not yet closed) */
  drawingPoints: Point[];
  /** Whether currently drawing */
  isDrawing: boolean;
}

export interface BoundaryCanvasHandle {
  /** Get the canvas element */
  getCanvas: () => HTMLCanvasElement | null;
}

/**
 * Canvas layer for drawing and displaying the custom clock boundary.
 * Renders both the active drawing path and the completed boundary.
 */
export const BoundaryCanvas = forwardRef<BoundaryCanvasHandle, BoundaryCanvasProps>(
  ({ width, height, boundaryPoints, drawingPoints, isDrawing }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useImperativeHandle(ref, () => ({
      getCanvas: () => canvasRef.current,
    }));

    // Redraw canvas when points change
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // getContext may return null in test environments (jsdom)
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw completed boundary if exists
      if (boundaryPoints && boundaryPoints.length > 2) {
        drawPath(ctx, boundaryPoints, BOUNDARY_STYLE, true);
      }

      // Draw current drawing path if actively drawing
      if (isDrawing && drawingPoints.length > 1) {
        drawPath(ctx, drawingPoints, DRAWING_STYLE, false);
      }
    }, [width, height, boundaryPoints, drawingPoints, isDrawing]);

    return (
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="boundary-canvas"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none', // Let mouse events pass through to container
        }}
      />
    );
  }
);

BoundaryCanvas.displayName = 'BoundaryCanvas';

/** Draw a path on the canvas */
function drawPath(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  style: { strokeColor: string; lineWidth: number },
  closePath: boolean
): void {
  if (points.length < 2) return;

  ctx.beginPath();
  ctx.strokeStyle = style.strokeColor;
  ctx.lineWidth = style.lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const firstPoint = points[0];
  if (firstPoint) {
    ctx.moveTo(firstPoint.x, firstPoint.y);
  }

  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (point) {
      ctx.lineTo(point.x, point.y);
    }
  }

  if (closePath) {
    ctx.closePath();
  }

  ctx.stroke();
}
